import { Box, Chip, Typography } from '@mui/material';
import useTemplateSx from '../../hooks/useTemplateSx';
import { LanguageList } from '../../utils/templateConstants';
import { getBodyPreviewText } from '../../utils/templateSendUtils';

function getLanguageLabel(code) {
  return LanguageList.find((item) => item.value === code)?.label ?? code ?? '—';
}

function formatMessage(text) {
  if (!text) return null;

  const elements = [];
  const regex = /(\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  const matches = [...text.matchAll(regex)];

  matches.forEach((match, index) => {
    const matchText = match[0];
    const start = match.index;

    if (start > lastIndex) {
      elements.push(text.slice(lastIndex, start));
    }

    if (matchText.startsWith('*') && matchText.endsWith('*')) {
      elements.push(<strong key={`bold-${index}`}>{matchText.slice(1, -1)}</strong>);
    } else if (matchText.startsWith('_') && matchText.endsWith('_')) {
      elements.push(<em key={`italic-${index}`}>{matchText.slice(1, -1)}</em>);
    }

    lastIndex = start + matchText.length;
  });

  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  return elements;
}

function getLiveBodyText(template, bodyParams = []) {
  const body = template?.components?.find((component) => component?.type === 'BODY');
  if (!body?.text) return '';

  return body.text.replace(/{{(\d+)}}/g, (match, number) => {
    const index = parseInt(number, 10) - 1;
    const filled = bodyParams?.[index]?.text?.trim();
    if (filled) return filled;
    return body.example?.body_text?.[0]?.[index] || match;
  });
}

export default function TemplateSendPreview({ template, bodyParams = [], compact = false }) {
  const styles = useTemplateSx();
  const mediaMaxHeight = compact ? 72 : 140;

  if (!template) return null;

  const headerComponent = template?.components?.find(
    (component) => String(component?.type || '').toUpperCase() === 'HEADER',
  );
  const headerType = headerComponent?.format
    ? String(headerComponent.format).toUpperCase()
    : null;
  const headerText = headerComponent?.text || '';
  const mediaUrl = template?.media?.header || null;
  const footerText = template?.components?.find((component) => component?.type === 'FOOTER')?.text || '';
  const buttons = template?.components?.find((component) => component?.type === 'BUTTONS')?.buttons ?? [];
  const bodyText = bodyParams.some((item) => item?.text?.trim())
    ? getLiveBodyText(template, bodyParams)
    : getBodyPreviewText(template) || 'Message preview will appear here.';

  const renderHeader = () => {
    if (!headerComponent) return null;

    const mediaFileName = template?.media?.headerFilename || '';

    if (headerType === 'IMAGE' && mediaUrl) {
      return (
        <Box
          sx={{
            mb: 1,
            mx: compact ? -1.125 : 0,
            mt: compact ? -1.125 : 0,
            borderRadius: compact ? '8px 8px 0 0' : '6px 6px 0 0',
            overflow: 'hidden',
            bgcolor: '#eef2f4',
          }}
        >
          <Box
            component="img"
            src={mediaUrl}
            alt="Header"
            sx={{
              width: '100%',
              maxHeight: compact ? 96 : mediaMaxHeight,
              display: 'block',
              objectFit: 'cover',
            }}
          />
        </Box>
      );
    }

    if (headerType === 'VIDEO' && mediaUrl) {
      return (
        <Box
          sx={{
            mb: 1,
            mx: compact ? -1.125 : 0,
            mt: compact ? -1.125 : 0,
            borderRadius: compact ? '8px 8px 0 0' : '6px 6px 0 0',
            overflow: 'hidden',
            bgcolor: '#000',
          }}
        >
          <Box
            component="video"
            controls
            playsInline
            sx={{
              width: '100%',
              maxHeight: compact ? 96 : 140,
              display: 'block',
              verticalAlign: 'middle',
              objectFit: 'cover',
              backgroundColor: '#000',
            }}
          >
            <source src={mediaUrl} type="video/mp4" />
          </Box>
        </Box>
      );
    }

    if (headerType === 'DOCUMENT' && mediaUrl) {
      const rawName = mediaFileName || mediaUrl.split('/').pop() || 'Header document';
      let fileName = rawName;
      try {
        fileName = decodeURIComponent(rawName);
      } catch {
        fileName = rawName;
      }
      return (
        <Box
          component="a"
          href={mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 0.75,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            bgcolor: 'rgba(18, 140, 126, 0.08)',
            border: '1px solid rgba(18, 140, 126, 0.25)',
            textDecoration: 'none',
            color: '#128C7E',
            maxWidth: '100%',
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: compact ? '0.6875rem' : '0.75rem',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fileName}
          >
            {fileName}
          </Box>
        </Box>
      );
    }

    if (headerType === 'TEXT' && headerText) {
      return (
        <Typography sx={{ ...styles.previewBubbleText, fontWeight: 700, mb: 0.75 }}>
          {headerText}
        </Typography>
      );
    }

    if (headerType && headerType !== 'TEXT') {
      return (
        <Typography sx={{ ...styles.previewBubbleText, fontStyle: 'italic', mb: 0.75 }}>
          [{headerType} header]
        </Typography>
      );
    }

    return null;
  };

  const cardSx = styles.previewCard;

  const headerSx = compact
    ? { ...styles.previewCardHeader, px: 1.25, py: 0.875 }
    : styles.previewCardHeader;

  const bodySx = compact
    ? { ...styles.previewCardBody, p: 1.25 }
    : styles.previewCardBody;

  const metaBlockSx = compact
    ? {
        mt: 0.75,
        pt: 0,
        borderTop: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
      }
    : styles.previewMetaBlock;

  const titleSx = compact
    ? { ...styles.previewCardTitle, fontSize: '0.8125rem', mb: compact ? 0 : undefined }
    : styles.previewCardTitle;

  const templateNameSx = compact
    ? { display: 'none' }
    : styles.previewTemplateName;

  const bubbleSx = compact
    ? { ...styles.previewBubble, p: 1.125, minHeight: 64 }
    : styles.previewBubble;

  const bubbleTextSx = compact
    ? { ...styles.previewBubbleText, fontSize: '0.75rem', lineHeight: 1.4 }
    : styles.previewBubbleText;

  const phoneHeaderSx = compact
    ? { ...styles.previewPhoneHeader, py: 0.625, px: 1.25 }
    : styles.previewPhoneHeader;

  return (
    <Box sx={cardSx}>
      <Box sx={headerSx}>
        <Typography sx={titleSx}>Message Preview</Typography>
        {!compact ? (
          <Typography sx={styles.previewCardSubtitle}>Live preview of template content</Typography>
        ) : null}

        <Box sx={metaBlockSx}>
          <Typography sx={templateNameSx}>{template.name}</Typography>
          <Box sx={{ ...styles.previewMetaChips, ...(compact ? { mt: 0 } : {}) }}>
            <Chip
              size="small"
              label={getLanguageLabel(template.language)}
              sx={styles.previewMetaChip}
              variant="outlined"
            />
            {template.bot_number ? (
              <Chip
                size="small"
                label={template.bot_number}
                sx={styles.previewMetaChip}
                variant="outlined"
              />
            ) : null}
            {template.category ? (
              <Chip
                size="small"
                label={template.category}
                sx={styles.previewMetaChip}
                color="primary"
                variant="outlined"
              />
            ) : null}
          </Box>
        </Box>
      </Box>
      <Box sx={bodySx}>
        <Box sx={styles.previewPhoneShell}>
          <Box sx={phoneHeaderSx}>
            <Typography sx={styles.previewPhoneHeaderTitle}>WhatsApp</Typography>
            <Typography sx={styles.previewPhoneHeaderTime}>now</Typography>
          </Box>
          <Box sx={bubbleSx}>
            {renderHeader()}
            <Typography sx={bubbleTextSx}>{formatMessage(bodyText)}</Typography>
            {footerText ? (
              <Typography sx={styles.previewFooterText}>{footerText}</Typography>
            ) : null}
            {buttons.length > 0 ? (
              <Box sx={styles.previewButtonsRow}>
                {buttons.map((button, index) => (
                  <Box
                    key={`${button.text}-${index}`}
                    sx={{
                      ...styles.previewButtonChip,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #128C7E',
                      borderRadius: 1,
                    }}
                  >
                    {button.text}
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
